import React, { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useApp } from '@/store/AppContext';
import { Grid3x3, User, Plus, X, Camera, Palette, BarChart3, Package, Monitor } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';
import { matchAppPath } from '@/i18n/localeRouting';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import AppsMenu from './AppsMenu';
import AccountMenu from '@/components/account/AccountMenu';
import ModuleSelector from './ModuleSelector';
import LanguageToggle from './LanguageToggle';
import moduleRegistry from '@/config/moduleRegistry';
import { ImageStyle } from '@/types';

const sidebarItemClass = (isActive: boolean, isDisabled: boolean) =>
  `w-full flex flex-col items-center gap-1 py-2.5 px-1 transition-all border-r-2 ${
    isDisabled
      ? 'opacity-50 cursor-not-allowed text-text-disabled border-transparent'
      : isActive
      ? 'border-white text-text-primary opacity-100'
      : 'border-transparent text-text-secondary opacity-80 hover:opacity-100 hover:text-text-primary'
  }`;

function getModuleIcon(moduleId: string, cls = 'w-6 h-6') {
  switch (moduleId) {
    case 'photo': return <Camera className={cls} strokeWidth={1.8} />;
    case 'illustration': return <Palette className={cls} strokeWidth={1.8} />;
    case 'infographic': return <BarChart3 className={cls} strokeWidth={1.8} />;
    case 'packshot': return <Package className={cls} strokeWidth={1.8} />;
    case 'mockup': return <Monitor className={cls} strokeWidth={1.8} />;
    default: return <Plus className={cls} strokeWidth={1.8} />;
  }
}

export default function ModeToggle() {
  const navigate = useAppNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isGenerating, isAuthenticated, isGuestMode, user, setActiveModule, setGalleryViewMode } = useApp();
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const canUseCreateModules = isAuthenticated || isGuestMode;
  const active =
    matchAppPath(location.pathname, '/create')
      ? 'create'
      : matchAppPath(location.pathname, '/browse') || matchAppPath(location.pathname, '/templates')
      ? 'browse'
      : matchAppPath(location.pathname, '/settings')
      ? 'settings'
      : matchAppPath(location.pathname, '/home')
      ? 'home'
      : 'none';
  const userInitials = user?.email
    ? user.email
        .split('@')[0]
        .split('.')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?';

  const handleModuleSelect = (moduleId: ImageStyle) => {
    if (isGenerating) return;
    setSheetOpen(false);
    setActiveModule(moduleId);
    const params = new URLSearchParams(searchParams);
    params.set('imageStyle', moduleId);
    if (moduleId === 'illustration') params.set('sceneType', 'action');
    navigate(`/create?${params.toString()}`);
  };

  const modules = Object.values(moduleRegistry).filter((m) => !m.disabled);

  const galleryButton = (
    <button
      onClick={() => {
        setGalleryViewMode('grid');
        navigate('/browse');
      }}
      disabled={isGenerating}
      className={sidebarItemClass(active === 'browse', isGenerating)}
      title={t('button.browse')}
    >
      <div className="w-10 h-10 flex items-center justify-center">
        <Grid3x3 className="w-6 h-6 shrink-0" strokeWidth={1.8} />
      </div>
      <span className="text-[11px] font-medium leading-none">{t('nav.gallery')}</span>
    </button>
  );

  const sidebarFooter = (
    <div className="mt-auto flex flex-col items-center gap-2 w-full px-1 py-3 border-t border-border">
      <AppsMenu openDirection="up" variant="sidebar" />
      {isAuthenticated && user ? (
        <AccountMenu />
      ) : (
        <button
          type="button"
          onClick={() => navigate('/signin')}
          className="w-full rounded-xl bg-bg-control border border-border hover:bg-bg-control-hover transition-all flex flex-col items-center gap-1.5 py-2 px-1"
          title={t('auth.signIn')}
        >
          <div className="w-6 h-6 rounded-full bg-bg-panel border border-border text-text-secondary flex items-center justify-center">
            <User className="w-3.5 h-3.5" strokeWidth={2} />
          </div>
          <span className="text-xs text-text-primary leading-tight">{t('auth.signIn')}</span>
        </button>
      )}
      <LanguageToggle variant="sidebar" disabled={isGenerating} />
    </div>
  );

  return (
    <>
      {/* Mobile top bar — simplified (no module tabs) */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-[90] bg-bg-panel border-b border-border pwa-header">
        <div className="h-12 flex items-center justify-between gap-2 px-3 min-w-0">
          <AppsMenu />
          <LanguageToggle disabled={isGenerating} />
        </div>
      </div>

      {/* Mobile bottom sheet overlay */}
      {sheetOpen && (
        <div
          className="md:hidden fixed inset-0 z-[150] bg-black/40 backdrop-blur-sm"
          onClick={() => setSheetOpen(false)}
        />
      )}

      {/* Mobile bottom sheet — sits at bottom:0, slides fully off-screen when closed */}
      <div
        className={`md:hidden fixed left-0 right-0 bottom-0 z-[160] bg-bg-panel rounded-t-2xl border-t border-border transition-transform duration-300 ease-out flex flex-col ${
          sheetOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{ maxHeight: '75vh' }}
        aria-hidden={!sheetOpen}
      >
        {/* drag handle */}
        <div className="flex justify-center pt-2.5 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
          <span className="text-sm font-semibold text-text-primary">{t('nav.create')}</span>
          <button
            type="button"
            onClick={() => setSheetOpen(false)}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-bg-control transition-colors"
          >
            <X className="w-4 h-4 text-text-secondary" strokeWidth={2} />
          </button>
        </div>
        {/* pb-16 reserves space for the bottom nav bar that the open sheet covers */}
        <div className="grid grid-cols-3 gap-3 p-4 overflow-y-auto pb-16">
          {modules.map((module) => (
            <button
              key={module.id}
              type="button"
              disabled={isGenerating}
              onClick={() => handleModuleSelect(module.id as ImageStyle)}
              className="flex flex-col items-center gap-2 py-4 px-2 rounded-xl bg-bg-control hover:bg-bg-control-hover active:scale-95 transition-all disabled:opacity-50"
            >
              <div className="w-11 h-11 rounded-full bg-bg-panel border border-border flex items-center justify-center text-text-primary">
                {getModuleIcon(module.id)}
              </div>
              <span className="text-xs font-medium text-text-primary text-center leading-tight">
                {module.name}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Desktop sidebar - full height from top-left */}
      <aside className="hidden md:flex w-[80px] h-screen bg-bg-panel border-r border-border flex-col shrink-0 z-[50]">
        <button
          type="button"
          onClick={() => navigate('/home')}
          className="w-full flex items-center justify-center py-4 border-b border-border shrink-0 transition-opacity hover:opacity-80"
          title={t('nav.home')}
        >
          <img src="/appicons/favicon.svg" alt="PC Studio" className="w-6 h-6 object-contain" />
        </button>

        {canUseCreateModules && (
          <div className="py-1 border-b border-border">
            <ModuleSelector variant="sidebar" />
          </div>
        )}

        <div className="py-1 border-b border-border">
          {galleryButton}
        </div>

        {sidebarFooter}
      </aside>

      {/* Mobile bottom tab bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-bg-panel border-t border-border h-14 mobile-bottom-nav">
        <div className="h-full grid grid-cols-3 items-center">
          {/* Gallery */}
          <button
            type="button"
            onClick={() => {
              if (isGenerating) return;
              setGalleryViewMode('grid');
              navigate('/browse');
            }}
            disabled={isGenerating}
            className={`flex flex-col items-center justify-center gap-1 h-full transition-all ${
              isGenerating ? 'opacity-50 cursor-not-allowed text-text-disabled'
              : active === 'browse' ? 'text-accent'
              : 'text-text-secondary'
            }`}
          >
            <Grid3x3 className="w-5 h-5" strokeWidth={1.8} />
            <span className="text-[9px] font-medium">{t('nav.gallery')}</span>
          </button>

          {/* + Create button */}
          <div className="flex items-center justify-center">
            <button
              type="button"
              disabled={isGenerating}
              onClick={() => !isGenerating && setSheetOpen((v) => !v)}
              className={`w-12 h-12 rounded-full bg-accent hover:bg-accent-hover active:scale-95 flex items-center justify-center shadow-lg transition-all ${
                isGenerating ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Plus
                className={`w-6 h-6 text-white transition-transform duration-200 ${sheetOpen ? 'rotate-45' : ''}`}
                strokeWidth={2.5}
              />
            </button>
          </div>

          {/* Account */}
          <button
            type="button"
            onClick={() => !isGenerating && (isAuthenticated ? navigate('/settings') : navigate('/signin'))}
            disabled={isGenerating}
            className={`flex flex-col items-center justify-center gap-1 h-full transition-all ${
              isGenerating ? 'opacity-50 cursor-not-allowed text-text-disabled'
              : active === 'settings' ? 'text-accent'
              : 'text-text-secondary'
            }`}
          >
            <div className={`w-6 h-6 rounded-full border flex items-center justify-center transition-colors ${
              active === 'settings' ? 'bg-accent-light border-accent/40' : 'bg-bg-control border-border'
            }`}>
              {isAuthenticated ? (
                <span className="text-[9px] font-semibold text-text-secondary">{userInitials}</span>
              ) : (
                <User className="w-3.5 h-3.5 text-text-secondary" strokeWidth={2} />
              )}
            </div>
            <span className="text-[9px] font-medium">{isAuthenticated ? t('nav.account') : t('auth.signIn')}</span>
          </button>
        </div>
      </div>
    </>
  );
}
